import { useState, useEffect } from 'react';
import { Building2, RefreshCw, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AtlassianAdapter } from '@/lib/hr-integrations/adapters/atlassian';
import { HRJob } from '@/lib/hr-integrations/types';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface AtlassianJobBoardProps {
  apiKey: string;
  endpoint: string;
  onImportComplete: () => void;
}

export function AtlassianJobBoard({ 
  apiKey, 
  endpoint, 
  onImportComplete 
}: AtlassianJobBoardProps) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<HRJob[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [existingJobs, setExistingJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      // Initialize Atlassian adapter
      const adapter = new AtlassianAdapter({ apiKey, endpoint });
      
      // Test connection
      const isConnected = await adapter.connect();
      if (!isConnected) {
        throw new Error('Failed to connect to Atlassian');
      }
      
      // Fetch jobs from Atlassian
      const atlassianJobs = await adapter.fetchJobs();
      setJobs(atlassianJobs);

      // Check which jobs are already imported
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) return;

      const { data: existingJobsData } = await supabase
        .from('jobs')
        .select('external_id')
        .eq('company_id', profile.company_id)
        .eq('external_source', 'Atlassian');

      const existingIds = new Set((existingJobsData || []).map(job => job.external_id));
      setExistingJobs(existingIds);
    } catch (error) {
      console.error('Error fetching Atlassian jobs:', error);
      toast.error('Failed to fetch jobs from Atlassian');
    } finally {
      setLoading(false);
    }
  };

  const handleImportSelected = async () => {
    if (selectedJobs.size === 0) {
      toast.error('Please select at least one job to import');
      return;
    }

    try {
      setImporting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('Company profile not found');
      }

      const selectedJobsList = jobs.filter(job => selectedJobs.has(job.externalId));
      let imported = 0;

      for (const job of selectedJobsList) {
        try {
          await supabase.from('jobs').insert([{
            company_id: profile.company_id,
            title: job.title,
            description: job.description,
            location: job.location,
            type: job.type,
            requirements: job.requirements,
            status: 'draft',
            external_source: job.source,
            external_id: job.externalId,
            external_url: job.sourceUrl
          }]);
          imported++;
        } catch (error) {
          console.error('Error importing job:', error);
        }
      }

      toast.success(`Successfully imported ${imported} jobs`);
      onImportComplete();
      
      // Refresh the job list to update existing jobs
      fetchJobs();
      setSelectedJobs(new Set());
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import jobs');
    } finally {
      setImporting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(job => job.externalId)));
    }
  };

  const toggleJobSelection = (externalId: string) => {
    const newSelection = new Set(selectedJobs);
    if (newSelection.has(externalId)) {
      newSelection.delete(externalId);
    } else {
      newSelection.add(externalId);
    }
    setSelectedJobs(newSelection);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold">Atlassian Jobs</CardTitle>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchJobs}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleImportSelected}
            disabled={importing || selectedJobs.size === 0}
          >
            {importing ? 'Importing...' : 'Import Selected'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Building2 className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No jobs found in Atlassian</p>
          </div>
        ) : (
          <>
            <div className="flex items-center space-x-2 mb-4">
              <Checkbox
                checked={selectedJobs.size === jobs.length}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm text-muted-foreground">
                {selectedJobs.size} of {jobs.length} selected
              </span>
            </div>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div
                    key={job.externalId}
                    className="flex items-start space-x-4 p-4 rounded-lg border bg-card"
                  >
                    <Checkbox
                      checked={selectedJobs.has(job.externalId)}
                      onCheckedChange={() => toggleJobSelection(job.externalId)}
                      disabled={existingJobs.has(job.externalId)}
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{job.title}</h3>
                        {existingJobs.has(job.externalId) ? (
                          <Badge variant="secondary">
                            <Check className="h-3 w-3 mr-1" />
                            Imported
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <X className="h-3 w-3 mr-1" />
                            Not Imported
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Badge variant="secondary">{job.type}</Badge>
                        <span>{job.location}</span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {job.description}
                      </p>
                      {job.requirements.length > 0 && (
                        <div className="text-sm">
                          <strong>Requirements:</strong>
                          <ul className="list-disc list-inside mt-1">
                            {job.requirements.slice(0, 3).map((req, index) => (
                              <li key={index} className="text-muted-foreground">
                                {req}
                              </li>
                            ))}
                            {job.requirements.length > 3 && (
                              <li className="text-muted-foreground">
                                +{job.requirements.length - 3} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      <div className="text-sm">
                        <a
                          href={job.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View in Atlassian
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </CardContent>
    </Card>
  );
}